/** @format */
import React, {Component, Suspense, lazy} from 'react';
import './sass/main.scss';
import {HashRouter as Router, Route, Switch} from 'react-router-dom';
import {LoginForm} from './login/LoginForm';
import {PasswordReset} from './login/PasswordReset';
import {ApolloProvider} from '@apollo/client';
import client, {loginHandler} from './apollo/client';
import DialogsDriver from './components/DialogsDriver/DialogsDriver';
import * as Sentry from '@sentry/browser';
import {ThemeProvider, createTheme, StyledEngineProvider} from '@mui/material/styles';
import {StylesProvider, createGenerateClassName} from '@mui/styles';
import orange from '@mui/material/colors/orange';
import {csCZ} from '@mui/material/locale';
import Rollbar from 'rollbar';
import {APP_RELEASE,initPerformanceDiagnostics,sanitizePerformanceEvent} from './diagnostics/performance';
import RouteTiming from './diagnostics/RouteTiming';
import {isSensitivePage, scrubSharingDiagnostics} from './sharing/privacy';

const Main = lazy(() => import('./containers/Main'));
const PublicShare = lazy(() => import('./sharing/PublicShare'));

const generateClassName = createGenerateClassName({
  productionPrefix: 'jss',
});

const theme = createTheme(
  {
    palette: {
      primary: {
        main: '#039be6',
      },
      secondary: orange,
    },
    status: {
      danger: 'orange',
    },
    typography: {
      fontFamily: ['Roboto', 'sans-serif'].join(','),
      fontSize: 22,
    },
    components: {
      MuiInputBase: {
        styleOverrides: {
          root: {
            paddingLeft: '10px',
            paddingRight: '10px',
          },
        },
      },
    },
  },
  csCZ
);

class App extends Component {
  constructor(props) {
    super(props);
    this.state = {loggedIn: loginHandler.isLoggedIn()};
  }

  handleLogin() {
    this.setState({loggedIn: true});
    this.router.history.replace('/');
  }

  handleLogout() {
    console.log('logout');
    loginHandler.logout({config: 'no-reload'});
    this.setState({loggedIn: false});
    this.router.history.replace('/');
    client.resetStore();
  }

  handleResetComplete() {
    loginHandler.logout({config:'no-reload'});
    this.setState({loggedIn:false});
    client.clearStore().catch(()=>{});
  }

  render() {
    const {loggedIn} = this.state;
    return (
      <StyledEngineProvider injectFirst>
        <StylesProvider generateClassName={generateClassName}>
          <ThemeProvider theme={theme}>
            <ApolloProvider client={client}>
              <DialogsDriver />
              <Router ref={router => (this.router = router)}>
                <Suspense fallback={<div className="login-wrapper">Načítání…</div>}>
                  <RouteTiming />
                  <Switch>
                    <Route exact path="/share/:token" component={PublicShare} />
                    <Route exact path="/login" component={() => <LoginForm onLogin={this.handleLogin.bind(this)} />} />
                    <Route
                      exact
                      path="/password-reset/:token?"
                      render={({match}) => <PasswordReset match={match} onReset={this.handleResetComplete.bind(this)} />}
                    />
                    <Route
                      path="/"
                      component={
                        loggedIn
                          ? () => <Main handleLogout={this.handleLogout.bind(this)} />
                          : () => <LoginForm onLogin={this.handleLogin.bind(this)} />
                      }
                    />
                  </Switch>
                </Suspense>
              </Router>
            </ApolloProvider>
          </ThemeProvider>
        </StylesProvider>
      </StyledEngineProvider>
    );
  }

  componentDidMount() {
    // A share URL is a bearer capability. Do not send it or positions to analytics.
    if (isSensitivePage()) return;
    if (process.env.NODE_ENV !== 'development') {
      var _rollbarConfig = {
        accessToken: 'f3cc73e164cd487bab52dfcd7daa8c16',
        captureUncaught: true,
        captureUnhandledRejections: true,
        reportLevel: 'error',
        checkIgnore: isSensitivePage,
        transform: payload => scrubSharingDiagnostics(payload),
        payload: {
          client: {javascript: {code_version: APP_RELEASE, source_map_enabled: true}},
          environment: 'production',
          person: {
            id: loginHandler.getEmail(),
          },
        },
      };

      new Rollbar(_rollbarConfig);
      if (this.state.loggedIn) {
        Sentry.setUser({username: loginHandler.getEmail()});
      }

      Sentry.addTracingExtensions();
      Sentry.init({
        dsn: 'https://d7de715dc2784027999260ef030ee327@sentry.io/1376248',
        environment: 'production',
        release: `react.gpson.cz@${APP_RELEASE}`,
        tracesSampleRate: 1,
        beforeSend: event => isSensitivePage() ? null : scrubSharingDiagnostics(event),
        beforeBreadcrumb: breadcrumb => isSensitivePage() || /\/(share|password-reset)\//.test(JSON.stringify(breadcrumb)) ? null : scrubSharingDiagnostics(breadcrumb),
        beforeSendTransaction: sanitizePerformanceEvent,
      });
      this.stopDiagnostics=initPerformanceDiagnostics();
    }
  }

  componentWillUnmount() {
    if(this.stopDiagnostics)this.stopDiagnostics();
  }
}

export default App;
